import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, User, Key, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface ResetPinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credentials: {
    username: string;
    pin: string;
  } | null;
}

const ResetPinDialog: React.FC<ResetPinDialogProps> = ({
  open,
  onOpenChange,
  credentials,
}) => {
  const [copiedField, setCopiedField] = useState<'username' | 'pin' | null>(null);

  const handleCopy = (field: 'username' | 'pin', value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success(`${field === 'username' ? 'Username' : 'PIN'} copied to clipboard`);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>PIN Reset Successful</DialogTitle>
          <DialogDescription>
            New PIN has been generated for the user
          </DialogDescription>
        </DialogHeader>

        {credentials ? (
          <>
            <div className="space-y-4 py-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  PIN reset successful! Please share these credentials with the client.
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Username</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 relative">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={credentials.username}
                        readOnly
                        className="pl-10 font-mono"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy('username', credentials.username)}
                    >
                      {copiedField === 'username' ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">New PIN</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 relative">
                      <Key className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={credentials.pin}
                        readOnly
                        className="pl-10 font-mono text-lg tracking-wider"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy('pin', credentials.pin)}
                    >
                      {copiedField === 'pin' ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  <strong>Important:</strong> Store these credentials safely. The PIN will not be visible again after closing this dialog.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button onClick={handleClose} className="w-full">
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            No credentials available
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResetPinDialog;
